// M24 — Advancements (ARCH §18). PURE (ENG-1): a static registry of nodes forming
// five trees (the root tabs Minecraft/Nether/End/Adventure/Husbandry), an
// event-driven trigger engine, and a graph-walk audit. The host fires triggers off
// the game event flow (dimension change, mine, obtain, kill, place, consume) and
// renders newly-granted advancements as toasts + a tree screen with our names.
//
// Faithful-but-representative slice: advancements complete when ALL their criteria
// have each fired at least once (Java's common single-criterion case; the array-of-
// OR-groups `requirements` form is deferred to M24-remainder). Advancements are
// INDEPENDENT — a child grants the moment its own criteria are met, regardless of
// whether the parent is done (parent links are the tree layout only, exactly as in
// Java). That is what makes "enter the nether fires once" clean.

/** A trigger kind + payload, produced by a game event. */
export type Trigger =
  | { kind: 'changed_dimension'; to: string }
  | { kind: 'obtain'; item: string } // an item entered the inventory (pickup/give/craft/smelt)
  | { kind: 'mine'; block: string } // a block was broken by the player
  | { kind: 'kill'; entity: string } // a mob was killed by the player
  | { kind: 'place'; block: string } // a block was placed by the player
  | { kind: 'consume'; item: string } // a food/potion was consumed
  | { kind: 'effect'; effect: string } // a status effect was applied
  // ── EDU-3 (ARCH §29.4; Part X, M51) — additive only; no existing member changes. ──
  | { kind: 'instrument_read'; instrument: string; metric: string; value: number }
  | { kind: 'notebook_record'; species: string }
  | { kind: 'region_entered'; region: string }
  | { kind: 'mob_bred'; species: string; recordedParents: boolean }
  | { kind: 'crop_harvested'; crop: string; produce: number }
  | { kind: 'beacon_found' }
  | { kind: 'aircraft_landed'; runway: boolean }
  | { kind: 'autopilot_hold'; ticks: number; error: number }
  // ── M53.5 (§29.12, CNT-5 `biology/population`) — additive only. ──
  | { kind: 'population_count'; species: string; value: number }
  | { kind: 'capacity_message' };

export type TriggerKind = Trigger['kind'];

export type FrameType = 'task' | 'goal' | 'challenge';

/** One condition of an advancement; `test` returns true when a trigger satisfies it. */
export interface Criterion {
  readonly id: string;
  readonly test: (t: Trigger) => boolean;
}

export interface AdvancementDef {
  readonly id: string;
  /** Parent in the tree, or null for a root tab. */
  readonly parent: string | null;
  readonly title: string;
  readonly description: string;
  readonly frame: FrameType;
  /** Item id shown as the toast/tree icon (must be a real registered item). */
  readonly icon: string;
  /** ALL must be satisfied for the advancement to complete. */
  readonly criteria: readonly Criterion[];
  readonly hidden?: boolean;
}

// ── Criterion constructors ─────────────────────────────────────────────────
const obtain = (item: string): Criterion => ({ id: `obtain:${item}`, test: (t) => t.kind === 'obtain' && t.item === item });
const mine = (block: string): Criterion => ({ id: `mine:${block}`, test: (t) => t.kind === 'mine' && t.block === block });
const kill = (entity: string): Criterion => ({ id: `kill:${entity}`, test: (t) => t.kind === 'kill' && t.entity === entity });
const place = (block: string): Criterion => ({ id: `place:${block}`, test: (t) => t.kind === 'place' && t.block === block });
const consume = (item: string): Criterion => ({ id: `consume:${item}`, test: (t) => t.kind === 'consume' && t.item === item });
const enterDim = (dim: string): Criterion => ({ id: `enter:${dim}`, test: (t) => t.kind === 'changed_dimension' && t.to === dim });
/** Matches any trigger of a kind (roots: "kill any mob", "eat any food"). */
const anyOf = (kind: TriggerKind): Criterion => ({ id: `any:${kind}`, test: (t) => t.kind === kind });

const node = (
  id: string,
  parent: string | null,
  title: string,
  description: string,
  icon: string,
  frame: FrameType,
  ...criteria: Criterion[]
): AdvancementDef => ({ id, parent, title, description, frame, icon, criteria });

// ── The registry: five trees (root tabs). Names are ours; structure follows Java. ──
export const ADVANCEMENTS: readonly AdvancementDef[] = [
  // ── Minecraft (story) ──
  node('story/root', null, 'Minecraft', 'The heart and story of the game', 'grass_block', 'task', anyOf('obtain')),
  node('story/mine_stone', 'story/root', 'Stone Age', 'Mine stone with your new pickaxe', 'cobblestone', 'task', obtain('cobblestone')),
  node('story/upgrade_tools', 'story/mine_stone', 'Getting an Upgrade', 'Construct a better pickaxe', 'stone_pickaxe', 'task', obtain('stone_pickaxe')),
  node('story/smelt_iron', 'story/upgrade_tools', 'Acquire Hardware', 'Smelt an iron ingot', 'iron_ingot', 'task', obtain('iron_ingot')),
  node('story/obtain_armor', 'story/smelt_iron', 'Suit Up', 'Protect yourself with a piece of iron armor', 'iron_chestplate', 'task', obtain('iron_chestplate')),
  node('story/lava_bucket', 'story/smelt_iron', 'Hot Stuff', 'Fill a bucket with lava', 'lava_bucket', 'task', obtain('lava_bucket')),
  node('story/mine_diamond', 'story/smelt_iron', 'Diamonds!', 'Acquire diamonds', 'diamond_ore', 'task', mine('diamond_ore')),
  node('story/enchant_item', 'story/mine_diamond', 'Enchanter', 'Construct an enchanting table', 'enchanting_table', 'task', obtain('enchanting_table')),
  node('story/enter_the_nether', 'story/mine_diamond', 'We Need to Go Deeper', 'Build, light and enter a Nether portal', 'flint_and_steel', 'task', enterDim('nether')),
  node('story/eye_spy', 'story/enter_the_nether', 'Eye Spy', 'Follow an Eye of Ender', 'ender_pearl', 'goal', obtain('ender_pearl')),
  node('story/enter_the_end', 'story/eye_spy', 'The End?', 'Enter the End portal', 'obsidian', 'task', enterDim('the_end')),

  // ── Nether ──
  node('nether/root', null, 'Nether', 'Bring summer clothes', 'netherrack', 'task', enterDim('nether')),
  node('nether/obtain_blaze_rod', 'nether/root', 'Into Fire', 'Relieve a Blaze of its rod', 'blaze_rod', 'task', obtain('blaze_rod')),
  node('nether/brew_potion', 'nether/obtain_blaze_rod', 'Local Brewery', 'Brew a potion', 'blaze_powder', 'task', obtain('blaze_powder')),
  node('nether/uneasy_alliance', 'nether/obtain_blaze_rod', 'Uneasy Alliance', 'Rescue a Ghast and bring it home', 'ghast_tear', 'challenge', obtain('ghast_tear')),
  node('nether/distract_piglin', 'nether/root', 'Oh Shiny', 'Distract Piglins with gold', 'gold_ingot', 'task', obtain('gold_ingot')),

  // ── The End ──
  node('end/root', null, 'The End', 'Or the beginning?', 'obsidian', 'task', enterDim('the_end')),
  node('end/kill_dragon', 'end/root', 'Free the End', 'Good luck', 'ender_pearl', 'goal', kill('ender_dragon')),
  node('end/elytra', 'end/kill_dragon', "Sky's the Limit", 'Find elytra', 'elytra', 'goal', obtain('elytra')),

  // ── Adventure ──
  node('adventure/root', null, 'Adventure', 'Adventure, exploration and combat', 'tnt', 'task', anyOf('kill')),
  node('adventure/kill_a_mob', 'adventure/root', 'Monster Hunter', 'Kill any hostile monster', 'stone_sword', 'task', kill('zombie')),
  node('adventure/trade', 'adventure/root', 'What a Deal!', 'Successfully trade with a Villager', 'emerald', 'task', obtain('emerald')),
  node('adventure/sleep_in_bed', 'adventure/root', 'Sweet Dreams', 'Sleep in a bed to change your respawn point', 'bed', 'task', place('bed')),
  node('adventure/totem_of_undying', 'adventure/root', 'Postmortal', 'Use a Totem of Undying to cheat death', 'totem_of_undying', 'goal', obtain('totem_of_undying')),

  // ── Husbandry ──
  node('husbandry/root', null, 'Husbandry', 'The world is full of friends and food', 'wheat', 'task', anyOf('consume')),
  node('husbandry/plant_seed', 'husbandry/root', 'A Seedy Place', 'Plant a seed and watch it grow', 'wheat_seeds', 'task', obtain('wheat_seeds')),
  node('husbandry/balanced_diet', 'husbandry/plant_seed', 'A Balanced Diet', 'Eat everything that is edible', 'apple', 'challenge', consume('apple')),
  node('husbandry/make_a_beehive', 'husbandry/root', 'Bee Our Guest', 'Get a beehive', 'beehive', 'task', obtain('beehive')),
  node('husbandry/fishy_business', 'husbandry/root', 'Fishy Business', 'Catch a fish', 'cod', 'task', obtain('cod')),
  node('husbandry/tactical_fishing', 'husbandry/fishy_business', 'Tactical Fishing', 'Catch a fish… without a fishing rod!', 'salmon', 'goal', obtain('salmon')),

  // ── Minecraft (story), expanded ──
  node('story/get_wood', 'story/root', 'Getting Wood', 'Punch a tree until a block of wood pops out', 'oak_log', 'task', obtain('oak_log')),
  node('story/benchmarking', 'story/get_wood', 'Benchmarking', 'Craft a crafting table', 'crafting_table', 'task', obtain('crafting_table')),
  node('story/time_to_mine', 'story/benchmarking', 'Time to Mine!', 'Craft a wooden pickaxe', 'wooden_pickaxe', 'task', obtain('wooden_pickaxe')),
  node('story/hot_topic', 'story/mine_stone', 'Hot Topic', 'Craft a furnace out of eight cobblestone', 'furnace', 'task', obtain('furnace')),
  node('story/coal_miner', 'story/mine_stone', 'Coal Miner', 'Mine a coal ore', 'coal_ore', 'task', mine('coal_ore')),
  node('story/copper_age', 'story/mine_stone', 'Copper Age', 'Smelt a copper ingot', 'copper_ingot', 'task', obtain('copper_ingot')),
  node('story/deeper_down', 'story/mine_stone', 'Deeper Down', 'Dig into the deepslate', 'deepslate', 'task', mine('deepslate')),
  node('story/light_it_up', 'story/get_wood', 'Light It Up', 'Place a torch to keep the dark away', 'torch', 'task', place('torch')),
  node('story/iron_man', 'story/obtain_armor', 'Iron Man', 'Hoard a full block of iron', 'iron_block', 'challenge', obtain('iron_block')),
  node('story/not_today', 'story/obtain_armor', 'Not Today, Thank You', 'Obtain a shield', 'shield', 'task', obtain('shield')),
  node('story/redstone_ready', 'story/smelt_iron', 'Redstone Ready', 'Mine some redstone dust', 'redstone', 'task', obtain('redstone')),
  node('story/repair_and_rename', 'story/smelt_iron', 'Repair & Rename', 'Craft an anvil', 'anvil', 'task', obtain('anvil')),
  node('story/librarian', 'story/enchant_item', 'Librarian', 'Build a bookshelf to power your table', 'bookshelf', 'task', obtain('bookshelf')),
  node('story/enchanted', 'story/enchant_item', 'Enchanted', 'Obtain an enchanted book', 'enchanted_book', 'goal', obtain('enchanted_book')),
  node('story/serious_dedication', 'story/mine_diamond', 'Serious Dedication', 'Mine an obsidian block', 'obsidian', 'challenge', obtain('obsidian')),

  // ── Nether, expanded ──
  node('nether/who_is_cutting_onions', 'nether/root', 'Who is Cutting Onions?', 'Obtain a Ghast Tear', 'ghast_tear', 'goal', obtain('ghast_tear')),
  node('nether/bright_lights', 'nether/root', 'Bright Lights', 'Harvest a block of glowstone', 'glowstone', 'task', obtain('glowstone')),
  node('nether/feels_like_home', 'nether/root', 'Feels Like Home', 'Obtain a magma block', 'magma_block', 'task', obtain('magma_block')),
  node('nether/sands_of_time', 'nether/root', 'Sands of Time', 'Gather some soul sand', 'soul_sand', 'task', obtain('soul_sand')),
  node('nether/soul_fire', 'nether/sands_of_time', 'Soul Fire', 'Craft a soul campfire', 'soul_campfire', 'task', obtain('soul_campfire')),
  node('nether/nether_quartz', 'nether/root', 'A Terrible Fortress', 'Break into a fortress and mine quartz', 'nether_quartz', 'task', obtain('nether_quartz')),
  node('nether/a_terrible_fortress', 'nether/brew_potion', 'Wart Farmer', 'Gather nether wart for brewing', 'nether_wart', 'task', obtain('nether_wart')),
  node('nether/bottoms_up', 'nether/brew_potion', 'Bottoms Up', 'Drink a potion', 'potion', 'goal', consume('potion')),
  node('nether/furious_cocktail', 'nether/brew_potion', 'A Furious Cocktail', 'Bottle up a splash potion', 'splash_potion', 'challenge', obtain('splash_potion')),
  node('nether/gold_rush', 'nether/distract_piglin', 'Gold Rush', 'Mine gold ore', 'gold_ore', 'task', mine('gold_ore')),
  node('nether/hidden_depths', 'nether/distract_piglin', 'Hidden in the Depths', 'Smelt magma cream', 'magma_cream', 'goal', obtain('magma_cream')),

  // ── The End, expanded ──
  node('end/great_view', 'end/elytra', 'Great View From Up Here', 'Launch a firework rocket while gliding', 'firework_rocket', 'challenge', obtain('firework_rocket')),

  // ── Adventure, expanded ──
  node('adventure/take_aim', 'adventure/root', 'Take Aim', 'Craft or find an arrow', 'arrow', 'task', obtain('arrow')),
  node('adventure/sniper_duel', 'adventure/kill_a_mob', 'Sniper Duel', 'Kill a skeleton', 'bone', 'goal', kill('skeleton')),
  node('adventure/boom', 'adventure/kill_a_mob', 'Boom!', 'Kill a creeper', 'gunpowder', 'task', kill('creeper')),
  node('adventure/arachnophobia', 'adventure/kill_a_mob', 'Arachnophobia', 'Kill a spider', 'string', 'task', kill('spider')),
  node('adventure/local_menace', 'adventure/kill_a_mob', 'Local Menace', 'Kill an enderman', 'ender_pearl', 'goal', kill('enderman')),
  node('adventure/witch_hunt', 'adventure/kill_a_mob', 'Witch Hunt', 'Kill a witch', 'glass_bottle', 'goal', kill('witch')),
  node('adventure/zombie_doctor', 'adventure/kill_a_mob', 'Zombie Doctor', 'Weaken and then cure a zombie villager', 'rotten_flesh', 'goal', obtain('golden_apple')),
  node('adventure/sticky_situation', 'adventure/root', 'Sticky Situation', 'Obtain a slimeball', 'slimeball', 'task', obtain('slimeball')),
  node('adventure/redstone_engineer', 'adventure/root', 'Redstone Engineer', 'Compact a block of redstone', 'redstone_block', 'task', obtain('redstone_block')),
  node('adventure/push_and_pull', 'adventure/redstone_engineer', 'Push and Pull', 'Build a piston', 'piston', 'task', obtain('piston')),
  node('adventure/i_spy', 'adventure/redstone_engineer', 'I Spy', 'Craft an observer', 'observer', 'task', obtain('observer')),
  node('adventure/automation', 'adventure/redstone_engineer', 'Automation', 'Assemble a crafter', 'crafter', 'goal', obtain('crafter')),
  node('adventure/play_a_tune', 'adventure/root', 'Play a Tune', 'Place a note block', 'note_block', 'task', place('note_block')),
  node('adventure/jukebox_hero', 'adventure/root', 'Jukebox Hero', 'Craft a jukebox', 'jukebox', 'task', obtain('jukebox')),
  node('adventure/ring_the_bell', 'adventure/root', 'Ring the Bell', 'Obtain a bell', 'bell', 'task', obtain('bell')),
  node('adventure/cartographer', 'adventure/root', 'Cartographer', 'Fill in a map of your surroundings', 'filled_map', 'task', obtain('filled_map')),
  node('adventure/getting_your_bearings', 'adventure/root', 'Getting Your Bearings', 'Craft a compass', 'compass', 'task', obtain('compass')),
  node('adventure/rod_and_reel', 'adventure/root', 'Rod & Reel', 'Craft a fishing rod', 'fishing_rod', 'task', obtain('fishing_rod')),
  node('adventure/demolition_expert', 'adventure/root', 'Demolition Expert', 'Prime a block of TNT', 'tnt', 'task', obtain('tnt')),
  node('adventure/amethyst_age', 'adventure/root', 'Amethyst Age', 'Collect an amethyst shard', 'amethyst_shard', 'task', obtain('amethyst_shard')),
  node('adventure/it_spreads', 'adventure/root', 'It Spreads', 'Recover a sculk catalyst from the deep dark', 'sculk_catalyst', 'goal', obtain('sculk_catalyst')),

  // ── Husbandry, expanded ──
  node('husbandry/bread_winner', 'husbandry/plant_seed', 'Bread Winner', 'Bake a loaf of bread', 'bread', 'task', obtain('bread')),
  node('husbandry/bake_a_cake', 'husbandry/plant_seed', 'Bake a Cake', 'Wheat, sugar, milk and eggs!', 'cake', 'task', obtain('cake')),
  node('husbandry/farm_to_table', 'husbandry/plant_seed', 'Farm to Table', 'Harvest a carrot', 'carrot', 'task', obtain('carrot')),
  node('husbandry/couch_potato', 'husbandry/plant_seed', 'Couch Potato', 'Bake a potato', 'baked_potato', 'task', obtain('baked_potato')),
  node('husbandry/beet_it', 'husbandry/plant_seed', 'Beet It', 'Cook up a bowl of beetroot soup', 'beetroot_soup', 'task', obtain('beetroot_soup')),
  node('husbandry/great_gourd', 'husbandry/plant_seed', 'Great Gourd', 'Bake a pumpkin pie', 'pumpkin_pie', 'task', obtain('pumpkin_pie')),
  node('husbandry/slice_of_life', 'husbandry/plant_seed', 'Slice of Life', 'Grow a melon', 'melon', 'task', obtain('melon')),
  node('husbandry/sweet_tooth', 'husbandry/plant_seed', 'Sweet Tooth', 'Refine sugar from cane', 'sugar', 'task', obtain('sugar')),
  node('husbandry/cocoa_beans', 'husbandry/plant_seed', 'Cocoa Farmer', 'Harvest cocoa beans', 'cocoa_beans', 'task', obtain('cocoa_beans')),
  node('husbandry/berry_picker', 'husbandry/root', 'Berry Picker', 'Pick sweet berries from a bush', 'sweet_berries', 'task', obtain('sweet_berries')),
  node('husbandry/waste_not', 'husbandry/plant_seed', 'Waste Not', 'Build a composter', 'composter', 'task', obtain('composter')),
  node('husbandry/bee_keeper', 'husbandry/make_a_beehive', 'Bee Keeper', 'Bottle some honey', 'honey_bottle', 'task', obtain('honey_bottle')),
  node('husbandry/total_beelocation', 'husbandry/make_a_beehive', 'Total Beelocation', 'Harvest honeycomb', 'honeycomb', 'goal', obtain('honeycomb')),
  node('husbandry/square_meal', 'husbandry/root', 'Square Meal', 'Eat a cooked steak', 'cooked_beef', 'task', consume('cooked_beef')),
  node('husbandry/poultry_plate', 'husbandry/root', 'Poultry Plate', 'Eat cooked chicken', 'cooked_chicken', 'task', consume('cooked_chicken')),
  node('husbandry/pork_dinner', 'husbandry/root', 'Pork Dinner', 'Eat a cooked porkchop', 'cooked_porkchop', 'task', consume('cooked_porkchop')),
  node('husbandry/mutton_bustin', 'husbandry/root', "Mutton Bustin'", 'Eat cooked mutton', 'cooked_mutton', 'task', consume('cooked_mutton')),
  node('husbandry/golden_bite', 'husbandry/balanced_diet', 'A Golden Bite', 'Eat a golden apple', 'golden_apple', 'goal', consume('golden_apple')),
  node('husbandry/fishermans_feast', 'husbandry/fishy_business', "Fisherman's Feast", 'Cook a fresh-caught fish', 'cooked_cod', 'task', obtain('cooked_cod')),
  node('husbandry/cow_tipper', 'husbandry/root', 'Cow Tipper', 'Gather leather', 'leather', 'task', obtain('leather')),
  node('husbandry/shear_delight', 'husbandry/root', 'Shear Delight', 'Collect a block of wool', 'white_wool', 'task', obtain('white_wool')),
  node('husbandry/egg_hunt', 'husbandry/root', 'Egg Hunt', 'Collect an egg from a chicken', 'egg', 'task', obtain('egg')),
  node('husbandry/cutest_predator', 'husbandry/root', 'The Cutest Predator', 'Scoop up an axolotl in a bucket', 'axolotl_bucket', 'goal', obtain('axolotl_bucket')),
  node('husbandry/powers_combined', 'husbandry/root', 'With Our Powers Combined!', 'Have a frog turn slime into a froglight', 'pearlescent_froglight', 'challenge', obtain('pearlescent_froglight')),
];

/** Registry indexed by id. */
export const ADVANCEMENT_BY_ID: ReadonlyMap<string, AdvancementDef> = new Map(ADVANCEMENTS.map((d) => [d.id, d]));

/** The five root tabs, in display order. */
export const ROOT_ADVANCEMENTS: readonly AdvancementDef[] = ADVANCEMENTS.filter((d) => d.parent === null);

// ── Serialized progress (persistence, ENG-9) ──
export interface AdvancementSave {
  readonly done: readonly string[];
  readonly criteria: readonly (readonly [string, readonly string[]])[];
}

/**
 * Tracks a player's advancement progress and grants advancements as triggers fire.
 * `fire` returns the ids newly completed by that trigger (the host toasts them).
 */
export class AdvancementTracker {
  /** Completed advancement ids. */
  readonly done = new Set<string>();
  /** advancementId → the set of its criterion ids satisfied so far. */
  private readonly satisfied = new Map<string, Set<string>>();

  constructor(private readonly defs: readonly AdvancementDef[] = ADVANCEMENTS) {}

  /** Whether an advancement is complete. */
  has(id: string): boolean {
    return this.done.has(id);
  }

  /** Fraction of an advancement's criteria satisfied (0..1) — tree progress bars. */
  progress(id: string): number {
    const def = this.defs.find((d) => d.id === id);
    if (!def || def.criteria.length === 0) return 0;
    const sat = this.satisfied.get(id);
    return (sat ? sat.size : 0) / def.criteria.length;
  }

  /**
   * Apply a trigger. Returns the ids of advancements that COMPLETED on this call
   * (empty if none, or if all matches were already complete — idempotent).
   */
  fire(t: Trigger): string[] {
    const newly: string[] = [];
    for (const def of this.defs) {
      if (this.done.has(def.id)) continue;
      let sat = this.satisfied.get(def.id);
      let changed = false;
      for (const c of def.criteria) {
        if (sat?.has(c.id)) continue;
        if (c.test(t)) {
          if (!sat) {
            sat = new Set();
            this.satisfied.set(def.id, sat);
          }
          sat.add(c.id);
          changed = true;
        }
      }
      if (changed && sat && def.criteria.every((c) => sat.has(c.id))) {
        this.done.add(def.id);
        newly.push(def.id);
      }
    }
    return newly;
  }

  toJSON(): AdvancementSave {
    return {
      done: [...this.done],
      criteria: [...this.satisfied].map(([id, s]) => [id, [...s]] as const),
    };
  }

  load(save: AdvancementSave | null | undefined): void {
    this.done.clear();
    this.satisfied.clear();
    if (!save) return;
    for (const id of save.done) this.done.add(id);
    for (const [id, crits] of save.criteria) this.satisfied.set(id, new Set(crits));
  }
}

// ── Graph-walk audit (M24 Verify: every advancement reachable from a root) ──
export interface AdvancementProblem {
  readonly id: string;
  readonly issue: string;
}

/** Ids reachable by walking parent→child from the roots (BFS). */
export function reachableAdvancements(defs: readonly AdvancementDef[] = ADVANCEMENTS): Set<string> {
  const children = new Map<string, string[]>();
  const roots: string[] = [];
  for (const d of defs) {
    if (d.parent === null) roots.push(d.id);
    else (children.get(d.parent) ?? children.set(d.parent, []).get(d.parent)!).push(d.id);
  }
  const reached = new Set<string>();
  const queue = [...roots];
  while (queue.length) {
    const id = queue.shift()!;
    if (reached.has(id)) continue;
    reached.add(id);
    for (const c of children.get(id) ?? []) queue.push(c);
  }
  return reached;
}

/**
 * Audit the registry: duplicate ids, missing parents, empty criteria, and any node
 * not reachable from a root (an orphan or a cycle). An empty result means the tree
 * is well-formed — the "every advancement reachable" graph walk.
 */
export function auditAdvancements(defs: readonly AdvancementDef[] = ADVANCEMENTS): AdvancementProblem[] {
  const problems: AdvancementProblem[] = [];
  const seen = new Set<string>();
  const ids = new Set(defs.map((d) => d.id));
  for (const d of defs) {
    if (seen.has(d.id)) problems.push({ id: d.id, issue: 'duplicate id' });
    seen.add(d.id);
    if (d.parent !== null && !ids.has(d.parent)) problems.push({ id: d.id, issue: `missing parent ${d.parent}` });
    if (d.criteria.length === 0) problems.push({ id: d.id, issue: 'no criteria' });
  }
  const reached = reachableAdvancements(defs);
  for (const d of defs) {
    if (!reached.has(d.id)) problems.push({ id: d.id, issue: 'not reachable from any root (orphan or cycle)' });
  }
  return problems;
}
